/**
 * DGM-Farbkalibrierungs-Service.
 *
 * Speichert min/max Höhenwerte pro Projekt und stellt eine client-seitige
 * Pixel-Transformation bereit, die auf dem Canvas des OfflineGridLayer
 * angewendet wird. Keine Server-seitige Verarbeitung pro Tile.
 *
 * Ansatz: Die Original-WMS-Farbrampe bildet Höhe auf einen schmalen
 * Grünbereich ab. Statt Reverse-Lookup (RGB→Höhe→neue Farbe) wird der
 * **relative Farbabstand** jedes Pixels zwischen der min-Farbe und
 * max-Farbe als Position auf der neuen Rampe verwendet. So bleibt die
 * volle Auflösung des Originals erhalten.
 *
 * Der einzige Server-Request ist /dgm/elevation (einmal pro Kalibrierung).
 */

import { Injectable, Inject, Optional, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PROJECT_PROVIDER } from '../../core/tokens';
import { ProjectProvider } from '../../core/interfaces';
import { DGM_API_URL } from '../../core/tokens';
import { OfflineGridLayer } from '../layers/offline-grid-layer';

export interface DgmCalibrationSettings {
	min: number;
	max: number;
}

// ─── Empirisch ermittelte Farbrampe des MV DGM-WMS ───────────────────────
// Wird nur genutzt um die RGB-Werte für min/max-Höhe nachzuschlagen.
const RAMP_POINTS = [
	{ h: -1,   r: 0,   g: 164, b: 32 },
	{ h: 0,    r: 0,   g: 165, b: 30 },
	{ h: 2,    r: 0,   g: 166, b: 27 },
	{ h: 4,    r: 0,   g: 168, b: 23 },
	{ h: 6,    r: 0,   g: 171, b: 17 },
	{ h: 8,    r: 0,   g: 173, b: 12 },
	{ h: 10,   r: 0,   g: 176, b: 7 },
	{ h: 12,   r: 0,   g: 177, b: 3 },
	{ h: 14,   r: 3,   g: 180, b: 0 },
	{ h: 17,   r: 12,  g: 184, b: 0 },
	{ h: 20,   r: 20,  g: 187, b: 0 },
	{ h: 24,   r: 31,  g: 191, b: 0 },
	{ h: 28,   r: 42,  g: 195, b: 0 },
	{ h: 31,   r: 51,  g: 198, b: 0 },
	{ h: 35,   r: 62,  g: 202, b: 0 },
	{ h: 38,   r: 75,  g: 207, b: 0 },
	{ h: 40,   r: 82,  g: 209, b: 0 },
	{ h: 42,   r: 89,  g: 211, b: 0 },
	{ h: 44,   r: 96,  g: 213, b: 0 },
	{ h: 46,   r: 103, g: 216, b: 0 },
	{ h: 47,   r: 107, g: 217, b: 0 },
	{ h: 48,   r: 111, g: 218, b: 0 },
	{ h: 49,   r: 114, g: 219, b: 0 },
	{ h: 50,   r: 118, g: 220, b: 0 },
	{ h: 51,   r: 122, g: 221, b: 0 },
	{ h: 52,   r: 126, g: 222, b: 0 },
	{ h: 53,   r: 129, g: 223, b: 0 },
	{ h: 54,   r: 132, g: 223, b: 0 },
	{ h: 56,   r: 140, g: 226, b: 0 },
	{ h: 57,   r: 145, g: 228, b: 0 },
	{ h: 58,   r: 149, g: 229, b: 0 },
	{ h: 59,   r: 153, g: 230, b: 0 },
	{ h: 60,   r: 154, g: 230, b: 1 },
	{ h: 61,   r: 156, g: 230, b: 1 },
	{ h: 62,   r: 157, g: 229, b: 2 },
	{ h: 63,   r: 158, g: 229, b: 2 },
	{ h: 64,   r: 159, g: 229, b: 2 },
	{ h: 65,   r: 161, g: 229, b: 3 },
	{ h: 66,   r: 163, g: 229, b: 4 },
	{ h: 68,   r: 166, g: 229, b: 5 },
	{ h: 70,   r: 167, g: 229, b: 5 },
	{ h: 72,   r: 170, g: 228, b: 7 },
	{ h: 74,   r: 174, g: 228, b: 8 },
	{ h: 76,   r: 176, g: 227, b: 9 },
	{ h: 78,   r: 179, g: 227, b: 10 },
	{ h: 80,   r: 181, g: 227, b: 11 },
	{ h: 82,   r: 184, g: 227, b: 12 },
	{ h: 84,   r: 186, g: 226, b: 14 },
	{ h: 86,   r: 189, g: 226, b: 15 },
	{ h: 88,   r: 190, g: 226, b: 15 },
	{ h: 90,   r: 194, g: 226, b: 17 },
	{ h: 93,   r: 197, g: 225, b: 18 },
	{ h: 97,   r: 202, g: 225, b: 21 },
	{ h: 101,  r: 205, g: 224, b: 22 },
	{ h: 105,  r: 210, g: 224, b: 24 },
	{ h: 113,  r: 218, g: 222, b: 29 },
	{ h: 121,  r: 216, g: 212, b: 30 },
];

// ─── Output-Farbrampe — minimales Blau, breites Grün-Gelb, breites Rot-Braun ─
const OUTPUT_RAMP = [
	{ t: 0.0,   r: 40,  g: 140, b: 210 },   // Blau-Cyan
	{ t: 0.05,  r: 40,  g: 200, b: 160 },   // Türkis
	{ t: 0.10,  r: 30,  g: 200, b: 60 },    // Dunkelgrün
	{ t: 0.17,  r: 50,  g: 215, b: 30 },    // Grün
	{ t: 0.24,  r: 100, g: 230, b: 25 },    // Hellgrün
	{ t: 0.31,  r: 155, g: 235, b: 25 },    // Gelbgrün
	{ t: 0.38,  r: 200, g: 240, b: 30 },    // Limette
	{ t: 0.45,  r: 230, g: 240, b: 35 },    // Gelblich-Grün
	{ t: 0.52,  r: 245, g: 240, b: 45 },    // Gelblich
	{ t: 0.59,  r: 250, g: 230, b: 50 },    // Gelb
	{ t: 0.64,  r: 255, g: 200, b: 40 },    // Goldgelb
	{ t: 0.70,  r: 255, g: 150, b: 25 },    // Orange
	{ t: 0.75,  r: 250, g: 100, b: 15 },    // Dunkles Orange
	{ t: 0.78,  r: 235, g: 60,  b: 15 },    // Orangerot
	{ t: 0.82,  r: 210, g: 30,  b: 10 },    // Rot
	{ t: 0.85,  r: 170, g: 20,  b: 20 },    // Dunkelrot
	{ t: 0.88,  r: 150, g: 40,  b: 20 },    // Rotbraun
	{ t: 0.91,  r: 140, g: 75,  b: 30 },    // Hellbraun
	{ t: 0.94,  r: 115, g: 85,  b: 45 },    // Warmbraun
	{ t: 0.97,  r: 85,  g: 70,  b: 45 },    // Mittelbraun
	{ t: 1.0,   r: 55,  g: 40,  b: 25 },    // Dunkelbraun
];

/**
 * Interpoliert RGB-Werte aus der Server-Rampe für eine gegebene Höhe.
 */
function rampColorForHeight(h: number): { r: number; g: number; b: number } {
	if (h <= RAMP_POINTS[0].h) return RAMP_POINTS[0];
	if (h >= RAMP_POINTS[RAMP_POINTS.length - 1].h) return RAMP_POINTS[RAMP_POINTS.length - 1];
	for (let i = 0; i < RAMP_POINTS.length - 1; i++) {
		const a = RAMP_POINTS[i], b = RAMP_POINTS[i + 1];
		if (h >= a.h && h <= b.h) {
			const f = (h - a.h) / (b.h - a.h);
			return {
				r: a.r + (b.r - a.r) * f,
				g: a.g + (b.g - a.g) * f,
				b: a.b + (b.b - a.b) * f,
			};
		}
	}
	return RAMP_POINTS[RAMP_POINTS.length - 1];
}

/** Berechnet die Ausgabefarbe für eine normierte Position (0-1) mit Dithering */
function elevationToColor(t: number): { r: number; g: number; b: number } {
	t = Math.max(0, Math.min(1, t));
	for (let i = 0; i < OUTPUT_RAMP.length - 1; i++) {
		const a = OUTPUT_RAMP[i], b = OUTPUT_RAMP[i + 1];
		if (t >= a.t && t <= b.t) {
			const f = (t - a.t) / (b.t - a.t);
			// Dithering: Rauschen (±2) um Banding aus der begrenzten WMS-Farbauflösung zu brechen
			const d = (Math.random() - 0.5) * 4;
			return {
				r: Math.max(0, Math.min(255, Math.round(a.r + (b.r - a.r) * f + d))),
				g: Math.max(0, Math.min(255, Math.round(a.g + (b.g - a.g) * f + d))),
				b: Math.max(0, Math.min(255, Math.round(a.b + (b.b - a.b) * f + d))),
			};
		}
	}
	const last = OUTPUT_RAMP[OUTPUT_RAMP.length - 1];
	return { r: last.r, g: last.g, b: last.b };
}

@Injectable({ providedIn: 'root' })
export class DgmCalibrationService {

	settings = signal<DgmCalibrationSettings | null>(null);
	available = computed(() => !!this.apiUrl);

	private readonly STORAGE_KEY = 'dgm_calibration';

	constructor(
		private http: HttpClient,
		@Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
		@Optional() @Inject(DGM_API_URL) private apiUrl: string | null,
	) {}

	async loadFromProject(): Promise<void> {
		try {
			const stored = await this.ps.getProjectValue(this.STORAGE_KEY) as DgmCalibrationSettings | undefined;
			this.settings.set(stored || null);
		} catch {
			this.settings.set(null);
		}
	}

	/** Kalibriert via Backend: sampelt Höhenwerte im angegebenen Gebiet (1 Request) */
	async calibrate(bbox: string): Promise<DgmCalibrationSettings> {
		if (!this.apiUrl) throw new Error('DGM API URL not configured');

		const result = await firstValueFrom(
			this.http.get<any>(`${this.apiUrl}/dgm/elevation`, {
				params: { bbox, grid: '20' }
			})
		);

		if (result.min == null || result.max == null) {
			throw new Error('Keine Höhendaten im angegebenen Gebiet');
		}

		const settings: DgmCalibrationSettings = { min: result.min, max: result.max };
		this.settings.set(settings);
		await this.ps.setProjectValue(this.STORAGE_KEY, settings);
		OfflineGridLayer.clearTileCache();
		return settings;
	}

	async reset(): Promise<void> {
		this.settings.set(null);
		await this.ps.setProjectValue(this.STORAGE_KEY, undefined);
		OfflineGridLayer.clearTileCache();
	}

	/**
	 * Erstellt eine Pixel-Transformationsfunktion für den OfflineGridLayer.
	 * Läuft komplett im Browser auf dem Canvas — keine Server-Belastung.
	 *
	 * Paletten-Bereich wird anhand der absoluten Höhe und der lokalen
	 * Differenz bestimmt:
	 * - Mittlere Höhe → Position auf der Palette (niedrig=blau, hoch=rot)
	 * - Lokale Differenz → Breite auf der Palette (min 30%, max 100%)
	 * - Kleine Differenzen (5-10m) nutzen nur einen passenden Farbabschnitt
	 */
	createPixelTransform(): ((imageData: ImageData) => void) | null {
		const s = this.settings();
		if (!s) return null;
		if (s.max <= s.min) return null;

		// RGB-Werte für min/max aus der Server-Rampe nachschlagen
		const cMin = rampColorForHeight(s.min);
		const cMax = rampColorForHeight(s.max);

		// Richtungsvektor im RGB-Raum von min-Farbe zu max-Farbe
		const dr = cMax.r - cMin.r;
		const dg = cMax.g - cMin.g;
		const db = cMax.b - cMin.b;
		const lenSq = dr * dr + dg * dg + db * db;

		if (lenSq === 0) return null;

		// ─── Paletten-Bereich anhand absoluter Höhe berechnen ─────────
		const GLOBAL_MAX = 120; // Maximale Höhe in MV
		const localRange = s.max - s.min;
		const centerHeight = (s.min + s.max) / 2;

		// Position auf der Palette (0-1) basierend auf absoluter Höhe
		const centerT = Math.max(0.05, Math.min(0.95, centerHeight / GLOBAL_MAX));

		// Breite: proportional zur Differenz, aber mindestens 30%, maximal 100%
		const paletteWidth = Math.max(0.3, Math.min(1.0, localRange / GLOBAL_MAX * 2.5));

		// Start/Ende auf der Palette (geclampet auf 0-1)
		const tStart = Math.max(0, centerT - paletteWidth / 2);
		const tEnd = Math.min(1, tStart + paletteWidth);
		// Korrigiere tStart falls tEnd an die Grenze stößt
		const finalStart = Math.max(0, tEnd - paletteWidth);

		// Vorberechnen: erwartete Helligkeit entlang der Rampe für schnellen Lookup
		// Für 256 Positionen (0-1) die erwartete Luminanz cachen
		const STEPS = 256;
		const expectedLum = new Float32Array(STEPS);
		for (let i = 0; i < STEPS; i++) {
			const t = i / (STEPS - 1);
			const h = s.min + t * localRange;
			const c = rampColorForHeight(h);
			expectedLum[i] = 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
		}

		return (imageData: ImageData) => {
			const data = imageData.data;
			for (let i = 0; i < data.length; i += 4) {
				const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];

				if (a === 0) continue;
				if (r > 250 && g > 250 && b > 250) continue;
				if (r < 5 && g < 5 && b < 5) continue;

				// 1. Projektion auf die Farbachse → Höhenposition (0-1)
				const pr = r - cMin.r;
				const pg = g - cMin.g;
				const pb = b - cMin.b;
				const rawT = Math.max(0, Math.min(1, (pr * dr + pg * dg + pb * db) / lenSq));

				// 2. Helligkeitsfaktor extrahieren (Schummerung)
				const pixelLum = 0.299 * r + 0.587 * g + 0.114 * b;
				const expLum = expectedLum[Math.round(rawT * (STEPS - 1))];
				const shade = expLum > 1 ? pixelLum / expLum : 1;

				// 3. Neue Farbe aus Output-Rampe
				const t = finalStart + rawT * (tEnd - finalStart);
				const color = elevationToColor(t);

				// 4. Schummerungsfaktor anwenden
				data[i]     = Math.max(0, Math.min(255, Math.round(color.r * shade)));
				data[i + 1] = Math.max(0, Math.min(255, Math.round(color.g * shade)));
				data[i + 2] = Math.max(0, Math.min(255, Math.round(color.b * shade)));
			}
		};
	}

	/** Prüft ob ein WMS-Layer ein DGM-Höhendarstellungs-Layer ist */
	isDgmElevationLayer(wmsUrl: string, wmsLayerName: string): boolean {
		const url = (wmsUrl || '').toLowerCase();
		const name = (wmsLayerName || '').toLowerCase();
		return (url.includes('dgm_wms') || url.includes('dgm/service'))
			&& (name.includes('hoehendarstellung') || name.includes('höhendarstellung'))
			&& !name.includes('schummerung');
	}
}
